import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Facebook, Instagram, Twitter, Mail, Phone, MapPin } from 'lucide-react';

export const Footer = () => {
  const { t, i18n } = useTranslation();
  const currentYear = new Date().getFullYear();
  const language = i18n.language?.startsWith('pt') ? 'pt' : 'en';

  // R2-2 FIX: Only show social icons if env vars are set, don't invent URLs
  const socialLinks = [
    { icon: Facebook, href: process.env.REACT_APP_SOCIAL_FACEBOOK, label: 'Facebook' },
    { icon: Instagram, href: process.env.REACT_APP_SOCIAL_INSTAGRAM, label: 'Instagram' },
    { icon: Twitter, href: process.env.REACT_APP_SOCIAL_TWITTER, label: 'Twitter' },
  ].filter(link => link.href && link.href.trim() !== '');

  return (
    <footer className="bg-[#08172F] text-white relative overflow-hidden" data-testid="footer">
      {/* Decorative gradient */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#00C2D1]/50 to-transparent" />
      
      <div className="container mx-auto px-4 py-12 md:py-16 relative">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Brand */}
          <div className="space-y-4">
            <Link to="/" className="inline-block">
              <motion.img 
                src="/logo.png" 
                alt="MagicStay Homes" 
                className="h-16 md:h-20 w-auto object-contain brightness-110"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.2 }}
              />
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed">
              {t('footer.disclaimer')}
            </p>
            {/* R2-2: Only render social icons if env vars are set */}
            {socialLinks.length > 0 && (
              <div className="flex gap-3">
                {socialLinks.map((social, index) => (
                  <motion.a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#00C2D1] transition-colors"
                    whileHover={{ scale: 1.1, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <social.icon className="w-5 h-5" />
                  </motion.a>
                ))}
              </div>
            )}
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-semibold text-lg mb-4 text-white">{t('footer.company')}</h3>
            <ul className="space-y-2">
              {[
                { to: '/search', label: t('navigation.searchProperties') },
                { to: '/host/create-property', label: t('navigation.plans') },
                { to: '/about', label: t('footer.aboutUs') },
                { to: '/how-it-works', label: t('footer.howItWorks') },
                { to: '/faq', label: t('footer.faq') },
              ].map((link, index) => (
                <li key={index}>
                  <Link 
                    to={link.to}
                    className="text-gray-400 hover:text-[#00C2D1] transition-colors text-sm inline-flex items-center gap-1 group"
                  >
                    <span className="group-hover:translate-x-1 transition-transform">{link.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal / Support */}
          <div>
            <h3 className="font-semibold text-lg mb-4 text-white">{t('footer.support')}</h3>
            <ul className="space-y-2">
              {[
                { to: '/faq', label: t('footer.helpCenter') },
                { to: '/terms', label: t('footer.termsOfUse') },
                { to: '/privacy', label: t('footer.privacyPolicy') },
              ].map((link, index) => (
                <li key={index}>
                  <Link 
                    to={link.to}
                    className="text-gray-400 hover:text-[#00C2D1] transition-colors text-sm inline-flex items-center gap-1 group"
                  >
                    <span className="group-hover:translate-x-1 transition-transform">{link.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-semibold text-lg mb-4 text-white">{t('navigation.contact')}</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 text-gray-400 text-sm">
                <MapPin className="w-5 h-5 text-[#00C2D1] flex-shrink-0 mt-0.5" />
                <span>{t('footer.location')}</span>
              </li>
              <li className="flex items-center gap-3 text-gray-400 text-sm">
                <Mail className="w-5 h-5 text-[#00C2D1] flex-shrink-0" />
                <a href="mailto:magicstayhomes@gmail.com" className="hover:text-[#00C2D1] transition-colors">
                  magicstayhomes@gmail.com
                </a>
              </li>
              <li className="flex items-center gap-3 text-gray-400 text-sm">
                <Phone className="w-5 h-5 text-[#00C2D1] flex-shrink-0" />
                <a href="tel:+13027241066" className="hover:text-[#00C2D1] transition-colors">
                  +1 (302) 724-1066
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-12 pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">
              © {currentYear} MagicStay Homes. {t('footer.allRightsReserved')}
            </p>
            <div className="flex items-center gap-2 text-gray-500 text-sm">
              {/* R1-4 FIX: Translate "Made with ❤️ in Orlando" */}
              <span>{language === 'pt' ? 'Feito com' : 'Made with'}</span>
              <motion.span
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 1, repeat: Infinity }}
                className="text-red-500"
              >
                ❤️
              </motion.span>
              <span>{language === 'pt' ? 'em Orlando' : 'in Orlando'}</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
